/**
 * spiceKindColor: the two colours SPICE sessions wear, one per kind,
 * wherever the kind is shown (the week grid's pills and legend, the
 * session editor's kind toggle, the schedule summary, My week).
 *
 * The owner chooses both fills in Admin (SPICE colours); they are stored
 * in tenants.config.spice.kind_colors and read here, each kind on its
 * own, with a fallback to that kind's default: SPICE the pepper token
 * (#C1512D, terracotta), Friendly Visitor the friendly-visitor token
 * (#2E5E9E, a clean mid blue). Both defaults carry white text at 4.5:1
 * or more (4.69:1 and 6.54:1).
 *
 * The text on a fill is white (the paper token) when white reads at
 * 4.5:1, else the ink (never pure black) when the ink does, else no text
 * colour reads on it: such a fill is refused in Admin and by the server,
 * and read here as absent, so no unreadable pill text can render even
 * from a hand-edited config.
 *
 * The server's mirror of the text rule is
 * api/_shared/validators/spiceKindColors.ts; the two agree over the table
 * shared by their tests. Pure; the useSpiceKindColors hook reads the
 * tenant config and hands it here.
 */

import { contrastRatio, isValidHexColor } from './mapColors';
import type { SpiceSessionKind } from './spiceVisitPlansApi';

/** Each kind's default fill: the values of --color-pepper and
 *  --color-friendly-visitor in src/index.css. */
export const DEFAULT_SPICE_KIND_COLORS: Record<SpiceSessionKind, string> = {
  spice: '#C1512D',
  spice_friendly_visitor: '#2E5E9E',
};

/** White pill text: the --color-paper token's value. */
export const SPICE_PILL_TEXT_LIGHT = '#FFFFFF';
/** Dark pill text: the --color-ink token's value. */
export const SPICE_PILL_TEXT_DARK = '#2D3A35';

/** WCAG AA for text. */
export const MIN_TEXT_CONTRAST = 4.5;

/** The text colour that reads on `fill`: white when white reaches 4.5:1,
 *  else the ink when it does, else null (no text colour reads on it). */
export function readableTextOn(fill: string): string | null {
  if (contrastRatio(SPICE_PILL_TEXT_LIGHT, fill) >= MIN_TEXT_CONTRAST) return SPICE_PILL_TEXT_LIGHT;
  if (contrastRatio(SPICE_PILL_TEXT_DARK, fill) >= MIN_TEXT_CONTRAST) return SPICE_PILL_TEXT_DARK;
  return null;
}

export interface SpiceKindColor {
  fill: string;
  text: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Both kinds' fill and text from a tenant config, each kind read on its
 *  own from config.spice.kind_colors[kind]: a value that is missing, not a
 *  #RRGGBB string, or carries no readable text falls back to that kind's
 *  default. Any other shape of the config gives both defaults. */
export function readSpiceKindColors(config: unknown): Record<SpiceSessionKind, SpiceKindColor> {
  const spice = isRecord(config) ? config['spice'] : undefined;
  const stored = isRecord(spice) ? spice['kind_colors'] : undefined;
  const pick = (kind: SpiceSessionKind): SpiceKindColor => {
    const value = isRecord(stored) ? stored[kind] : undefined;
    if (typeof value === 'string' && isValidHexColor(value)) {
      const text = readableTextOn(value);
      if (text !== null) return { fill: value, text };
    }
    const fallback = DEFAULT_SPICE_KIND_COLORS[kind];
    return { fill: fallback, text: readableTextOn(fallback) ?? SPICE_PILL_TEXT_DARK };
  };
  return { spice: pick('spice'), spice_friendly_visitor: pick('spice_friendly_visitor') };
}
